import React, { useEffect, useState } from 'react';
import { useHeader } from '../../context/HeaderContext';
import { getSupportTicketDetails } from '../../api/support';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdArrowBack } from 'react-icons/md';

const TicketDetails = () => {
  const { setHeaderTitle } = useHeader();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);

  useEffect(() => {
    setHeaderTitle('Ticket Details');
    if (id) {
      fetchTicketDetails();
    }
  }, [setHeaderTitle, id]);

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      const data = await getSupportTicketDetails(id);
      setTicket(data);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      toast.error('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'open' || statusLower === 'pending') {
      return <span className='px-4 py-2 bg-chipYellow text-white rounded-full text-base font-semibold'>Pending</span>;
    } else if (statusLower === 'resolved' || statusLower === 'closed') {
      return <span className='px-4 py-2 bg-chipGreen text-white rounded-full text-base font-semibold'>Resolved</span>;
    } else if (statusLower === 'in progress') {
      return <span className='px-4 py-2 bg-chipBlue text-white rounded-full text-base font-semibold'>In Progress</span>;
    }
    return <span className='px-4 py-2 bg-gray-400 text-white rounded-full text-base font-semibold'>{status || 'Pending'}</span>;
  };

  const getPriorityBadge = (priority) => {
    const priorityLower = (priority || '').toLowerCase();
    if (priorityLower === 'high' || priorityLower === 'urgent') {
      return <span className='px-3 py-1 bg-chipRed text-white rounded-full text-sm font-semibold'>{priority}</span>;
    } else if (priorityLower === 'medium') {
      return <span className='px-3 py-1 bg-chipYellow text-white rounded-full text-sm font-semibold'>{priority}</span>;
    } else if (priorityLower === 'low') {
      return <span className='px-3 py-1 bg-chipGreen text-white rounded-full text-sm font-semibold'>{priority}</span>;
    }
    return <span className='px-3 py-1 bg-gray-400 text-white rounded-full text-sm font-semibold'>{priority || 'N/A'}</span>;
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-20'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className='p-6'>
        <div className='text-center py-20'>
          <p className='text-lg text-gray-500'>Ticket not found</p>
          <button
            onClick={() => navigate('/support')}
            className='mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primaryHoverDark'
          >
            Back to Support Tickets
          </button>
        </div>
        <ToastContainer position="bottom-right" autoClose={2000} />
      </div>
    );
  }

  return (
    <div className='w-full'>
      <section className='bg-white rounded-md shadow-sm p-6'>
        <button
          onClick={() => navigate('/support')}
          className='flex items-center gap-2 text-primary hover:text-primaryHoverDark mb-4 font-medium'
        >
          <MdArrowBack className='text-xl' />
          Back to Support Tickets
        </button>

        {/* Header */}
        <div className='flex justify-between items-start mb-6 pb-6 border-b border-gray-200'>
          <div>
            <h1 className='text-2xl font-bold text-gray-900 mb-2'>
              {ticket.subject || ticket.title || 'Support Ticket'}
            </h1>
            <p className='text-sm text-gray-600'>
              Ticket #{ticket._id || ticket.id} • Created on {formatDate(ticket.createdAt)}
            </p>
          </div>
          {getStatusBadge(ticket.status)}
        </div>

        {/* Ticket Metadata */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-6'>
          <div>
            <label className='block text-sm font-medium text-gray-500 mb-1'>Issue Type</label>
            <p className='text-base text-gray-900 font-semibold'>{ticket.issueType || 'N/A'}</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-500 mb-1'>Priority</label>
            <div>{getPriorityBadge(ticket.priority)}</div>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-500 mb-1'>Status</label>
            <p className='text-base text-gray-900'>{ticket.status || 'Pending'}</p>
          </div>
        </div>

        {/* Related Campaign */}
        {ticket.campaign && (
          <div className='mb-6'>
            <label className='block text-sm font-medium text-gray-500 mb-2'>Related Campaign</label>
            <div className='bg-gray-50 rounded-md p-4'>
              <h3 className='text-base font-semibold text-gray-900'>
                {ticket.campaign.title || ticket.campaign.campaignTitle || 'N/A'}
              </h3>
              {ticket.campaign.campaignType && (
                <p className='text-sm text-gray-600 mt-1'>Type: {ticket.campaign.campaignType}</p>
              )}
            </div>
          </div>
        )}

        {/* Issue Description */}
        <div className='mb-6'>
          <label className='block text-sm font-medium text-gray-500 mb-2'>Description</label>
          <div className='bg-gray-50 rounded-md p-4'>
            <p className='text-base text-gray-900 whitespace-pre-wrap'>
              {ticket.description || ticket.message || 'N/A'}
            </p>
          </div>
        </div>

        {/* Uploaded Image */}
        {ticket.image && (
          <div className='mb-6'>
            <label className='block text-sm font-medium text-gray-500 mb-3'>Attached Image</label>
            <div className='bg-gray-50 rounded-lg p-4 border border-gray-200'>
              <img
                src={ticket.image}
                alt="Support Ticket Attachment"
                className='max-w-full h-auto rounded-lg shadow-sm cursor-pointer hover:opacity-90 transition-opacity'
                style={{ maxHeight: '400px', objectFit: 'contain' }}
                onClick={() => window.open(ticket.image, '_blank')}
              />
              <p className='text-xs text-gray-500 mt-2'>Click image to view full size</p>
            </div>
          </div>
        )}

        {/* Conversation Thread / Responses */}
        {ticket.responses && ticket.responses.length > 0 && (
          <div className='mb-6 pt-6 border-t border-gray-200'>
            <label className='block text-sm font-medium text-gray-500 mb-4'>Conversation</label>
            <div className='space-y-4'>
              {ticket.responses.map((response, index) => (
                <div
                  key={response._id || response.id || index}
                  className={`rounded-md p-4 ${
                    response.isAdmin
                      ? 'bg-blue-50 border border-blue-200'
                      : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className='flex justify-between items-start mb-2'>
                    <span className='font-semibold text-gray-900'>
                      {response.isAdmin ? 'Support Team' : 'You'}
                    </span>
                    <span className='text-sm text-gray-600'>{formatDate(response.createdAt)}</span>
                  </div>
                  <p className='text-base text-gray-900 whitespace-pre-wrap'>{response.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admin Response (if not using conversation thread) */}
        {!ticket.responses && ticket.adminResponse && (
          <div className='pt-6 border-t border-gray-200'>
            <label className='block text-sm font-medium text-gray-500 mb-2'>Support Team Response</label>
            <div className='bg-blue-50 border border-blue-200 rounded-md p-4'>
              <p className='text-base text-gray-900 whitespace-pre-wrap'>{ticket.adminResponse}</p>
              {ticket.respondedAt && (
                <p className='text-sm text-gray-600 mt-2'>Responded on {formatDate(ticket.respondedAt)}</p>
              )}
            </div>
          </div>
        )}

        {/* Resolution Details */}
        {(ticket.status?.toLowerCase() === 'resolved' || ticket.status?.toLowerCase() === 'closed') && ticket.resolvedAt && (
          <div className='mt-6 pt-6 border-t border-gray-200'>
            <div className='bg-green-50 border border-green-200 rounded-md p-4'>
              <h3 className='font-semibold text-gray-900 mb-1'>Ticket Resolved</h3>
              <p className='text-sm text-gray-600'>Resolved on {formatDate(ticket.resolvedAt)}</p>
              {ticket.resolutionNote && (
                <p className='text-base text-gray-900 mt-2'>{ticket.resolutionNote}</p>
              )}
            </div>
          </div>
        )}

        {/* Ticket Timeline */}
        <div className='mt-6 pt-6 border-t border-gray-200'>
          <label className='block text-sm font-medium text-gray-500 mb-4'>Timeline</label>
          <div className='space-y-3'>
            <div className='flex items-start gap-3'>
              <div className='w-2 h-2 bg-primary rounded-full mt-2'></div>
              <div>
                <p className='text-sm font-medium text-gray-900'>Ticket Created</p>
                <p className='text-xs text-gray-600'>{formatDate(ticket.createdAt)}</p>
              </div>
            </div>
            {ticket.updatedAt && ticket.updatedAt !== ticket.createdAt && (
              <div className='flex items-start gap-3'>
                <div className='w-2 h-2 bg-chipBlue rounded-full mt-2'></div>
                <div>
                  <p className='text-sm font-medium text-gray-900'>Last Updated</p>
                  <p className='text-xs text-gray-600'>{formatDate(ticket.updatedAt)}</p>
                </div>
              </div>
            )}
            {ticket.resolvedAt && (
              <div className='flex items-start gap-3'>
                <div className='w-2 h-2 bg-chipGreen rounded-full mt-2'></div>
                <div>
                  <p className='text-sm font-medium text-gray-900'>Ticket Resolved</p>
                  <p className='text-xs text-gray-600'>{formatDate(ticket.resolvedAt)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
};

export default TicketDetails;
